import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  User, 
  Users, 
  CreditCard,
  Calendar,
  Mail,
  Phone,
  FileText,
  AlertCircle,
  Info,
  UserPlus,
  Receipt,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import toast from 'react-hot-toast';
import { dataHelpers } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const MonthlySubscriberForm = ({ onSubmit, onCancel, initialData = null, loading = false }) => {
  const { company } = useAuth();
  
  const [formData, setFormData] = useState({
    // Dados do estudante
    student_id: initialData?.student_id || '',
    monthly_plan_id: initialData?.monthly_plan_id || '',
    
    // Código do contrato
    contract_code: initialData?.contract_code || '',
    
    // Dados da assinatura
    start_date: initialData?.start_date || new Date().toISOString().split('T')[0],
    end_date: initialData?.end_date || '',
    status: initialData?.status || 'active',
    
    // Seleção de responsável (sempre obrigatório)
    selected_parent_id: initialData?.selected_parent_id || '',
    
    // Dados do responsável financeiro
    payer_name: initialData?.payer_name || '',
    payer_email: initialData?.payer_email || '',
    payer_phone: initialData?.payer_phone || '',
    payer_cpf: initialData?.payer_cpf || '',
    
    // Observações
    notes: initialData?.notes || ''
  });

  const [errors, setErrors] = useState({});
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [availablePeople, setAvailablePeople] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [invoices, setInvoices] = useState([]);

  // Carregar dados necessários
  useEffect(() => {
    if (company?.id) {
      loadInitialData();
    }
  }, [company]);

  // Carregar faturas quando assinatura for carregada (modo edição)
  useEffect(() => {
    if (initialData?.id) {
      loadSubscriberInvoices();
    }
  }, [initialData]);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      await Promise.all([
        loadStudents(),
        loadPlans(),
        loadAvailablePeople()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoadingData(false);
    }
  };

  const loadStudents = async () => {
    try {
      const data = await dataHelpers.getStudents(company.id);
      setStudents(data || []);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadPlans = async () => {
    try {
      const data = await dataHelpers.getMonthlyPlans(company.id);
      setPlans(data?.filter(plan => plan.active) || []);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadAvailablePeople = async () => {
    try {
      const people = await dataHelpers.getPeople(company.id);
      console.log('All people loaded:', people);
      // Filtrar apenas pessoas ativas do tipo "parent" que podem ser responsáveis
      const availablePeople = people.filter(person => 
        person.active && person.type === 'parent'
      );
      console.log('Available parents:', availablePeople);
      setAvailablePeople(availablePeople || []);
    } catch (error) {
      console.error('Error loading people:', error);
    }
  };

  const loadSubscriberInvoices = async () => {
    try {
      const data = await dataHelpers.getMonthlyInvoices(company.id);
      // Filtrar faturas desta assinatura
      const subscriberInvoices = data.filter(invoice => 
        invoice.monthly_subscriber_id === initialData.id
      );
      setInvoices(subscriberInvoices || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const handleParentSelectionChange = (selectedParentId) => {
    if (selectedParentId) {
      const selectedPerson = availablePeople.find(person => person.id === selectedParentId);
      if (selectedPerson) {
        console.log('🔍 handleParentSelectionChange - Selected person data:', selectedPerson);
        console.log('📄 handleParentSelectionChange - Document from person:', selectedPerson.document);
        console.log('🆔 handleParentSelectionChange - CPF formatted:', formatCPF(selectedPerson.document || ''));
        setFormData(prev => ({
          ...prev,
          selected_parent_id: selectedParentId,
          payer_name: selectedPerson.name,
          payer_email: selectedPerson.email,
          payer_phone: selectedPerson.phone || '',
          payer_cpf: formatCPF(selectedPerson.document || ''),
          asaas_customer_id: selectedPerson.asaas_customer_id || ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        selected_parent_id: '',
        payer_name: '',
        payer_email: '',
        payer_phone: '',
        payer_cpf: '',
        asaas_customer_id: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Formatação específica para campos
    if (name === 'payer_phone') {
      newValue = formatPhone(value);
    } else if (name === 'payer_cpf') {
      newValue = formatCPF(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Se selecionou um aluno, automaticamente usar dados do responsável
    if (name === 'student_id' && value) {
      const selectedStudent = students.find(s => s.id === value);
      if (selectedStudent && selectedStudent.person) {
        // Usar dados do responsável principal da tabela people
        const responsiblePerson = selectedStudent.person;
        console.log('🎯 Using responsible person from people table:', responsiblePerson);
        
        setFormData(prev => ({
          ...prev,
          selected_parent_id: responsiblePerson.id,
          payer_name: responsiblePerson.name,
          payer_email: responsiblePerson.email,
          payer_phone: responsiblePerson.phone || '',
          payer_cpf: formatCPF(responsiblePerson.cpf || responsiblePerson.document || ''),
          asaas_customer_id: responsiblePerson.asaas_customer_id || ''
        }));
      } else if (selectedStudent && selectedStudent.financial_person) {
        // Se não tem responsável principal, usar responsável financeiro
        const financialPerson = selectedStudent.financial_person;
        console.log('🎯 Using financial person from people table:', financialPerson);
        
        setFormData(prev => ({
          ...prev,
          selected_parent_id: financialPerson.id,
          payer_name: financialPerson.name,
          payer_email: financialPerson.email,
          payer_phone: financialPerson.phone || '',
          payer_cpf: formatCPF(financialPerson.cpf || financialPerson.document || ''),
          asaas_customer_id: financialPerson.asaas_customer_id || ''
        }));
      } else {
        console.log('⚠️ No responsible person found for student');
        // Limpar campos se não encontrar responsável
        setFormData(prev => ({
          ...prev,
          selected_parent_id: '',
          payer_name: '',
          payer_email: '',
          payer_phone: '',
          payer_cpf: '',
          asaas_customer_id: ''
        }));
      }
    }

    // Se selecionou um responsável financeiro, preencher dados automaticamente
    if (name === 'selected_parent_id' && value) {
      const selectedPerson = availablePeople.find(person => person.id === value);
      if (selectedPerson) {
        console.log('🔍 handleChange - Selected person data:', selectedPerson);
        console.log('📄 handleChange - Document from person:', selectedPerson.document);
        console.log('🆔 handleChange - CPF formatted:', formatCPF(selectedPerson.document || ''));
        setFormData(prev => ({
          ...prev,
          selected_parent_id: value,
          payer_name: selectedPerson.name,
          payer_email: selectedPerson.email,
          payer_phone: selectedPerson.phone || '',
          payer_cpf: formatCPF(selectedPerson.document || ''),
          asaas_customer_id: selectedPerson.asaas_customer_id || ''
        }));
      }
    }

    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-gerar código do contrato se não existe
    if (name === 'student_id' && value && !formData.contract_code) {
      const selectedStudent = students.find(s => s.id === value);
      if (selectedStudent) {
        const contractCode = generateContractCode(selectedStudent);
        setFormData(prev => ({
          ...prev,
          contract_code: contractCode
        }));
      }
    }

    // (Removida lógica duplicada - já é tratada acima na seleção do estudante)
  };

  const generateContractCode = (student) => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const day = String(new Date().getDate()).padStart(2, '0');
    const hour = String(new Date().getHours()).padStart(2, '0');
    const minute = String(new Date().getMinutes()).padStart(2, '0');
    
    // Use random 6-character suffix for uniqueness
    const randomSuffix = Math.random().toString(36).substr(2, 6).toUpperCase();
    return `MENS${year}${month}${day}${hour}${minute}${randomSuffix}`;
  };

  // Check if contract code is unique
  const isContractCodeUnique = async (contractCode) => {
    try {
      const data = await dataHelpers.checkContractCodeUniqueness(company.id, contractCode);
      return data.isUnique;
    } catch (error) {
      console.error('Error checking contract code uniqueness:', error);
      return false;
    }
  };

  // Generate unique contract code with retry mechanism
  const generateUniqueContractCode = async (student, maxRetries = 5) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const contractCode = generateContractCode(student);
      const isUnique = await isContractCodeUnique(contractCode);
      
      if (isUnique) {
        return contractCode;
      }
      
      // Wait a bit before retry to ensure different timestamp
      await new Promise(resolve => setTimeout(resolve, 100 + attempt * 50));
    }
    
    // Fallback: add random suffix if all retries failed
    const baseCode = generateContractCode(student);
    const randomSuffix = Math.random().toString(36).substr(2, 4).toUpperCase();
    return `${baseCode}${randomSuffix}`;
  };

  const handleRegenerateContractCode = async () => {
    if (!formData.student_id) {
      toast.error('Selecione um estudante primeiro');
      return;
    }

    const selectedStudent = students.find(s => s.id === formData.student_id);
    if (!selectedStudent) {
      toast.error('Estudante não encontrado');
      return;
    }

    try {
      const loadingToast = toast.loading('Gerando novo código de contrato...');
      const newContractCode = await generateUniqueContractCode(selectedStudent);
      
      setFormData(prev => ({
        ...prev,
        contract_code: newContractCode
      }));
      
      toast.dismiss(loadingToast);
      toast.success('Novo código de contrato gerado');
    } catch (error) {
      console.error('Error regenerating contract code:', error);
      toast.error('Erro ao gerar novo código');
    }
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const validateForm = () => {
    const newErrors = {};

    // Validações obrigatórias
    if (!formData.student_id) {
      newErrors.student_id = 'Estudante é obrigatório';
    }

    if (!formData.monthly_plan_id) {
      newErrors.monthly_plan_id = 'Plano de mensalidade é obrigatório';
    }

    if (!formData.contract_code.trim()) {
      newErrors.contract_code = 'Código do contrato é obrigatório';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Data de início é obrigatória';
    }

    // Validação do responsável (sempre obrigatório)
    if (!formData.selected_parent_id) {
      newErrors.selected_parent_id = 'Selecione um responsável financeiro';
    }

    if (!formData.payer_name.trim()) {
      newErrors.payer_name = 'Nome do responsável é obrigatório';
    }

    if (!formData.payer_email.trim()) {
      newErrors.payer_email = 'Email do responsável é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.payer_email)) {
      newErrors.payer_email = 'Email inválido';
    }

    if (!formData.payer_cpf.trim()) {
      newErrors.payer_cpf = 'CPF do responsável é obrigatório';
    } else if (formData.payer_cpf.replace(/\D/g, '').length !== 11) {
      newErrors.payer_cpf = 'CPF deve ter 11 dígitos';
    }

    // Validação de datas
    if (formData.end_date && formData.start_date && formData.end_date <= formData.start_date) {
      newErrors.end_date = 'Data de fim deve ser posterior à data de início';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      // Preparar dados para envio
      const subscriberData = {
        student_id: formData.student_id,
        monthly_plan_id: formData.monthly_plan_id,
        contract_code: formData.contract_code.trim(),
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        status: formData.status,
        selected_parent_id: formData.selected_parent_id,
        payer_name: formData.payer_name.trim(),
        payer_email: formData.payer_email.trim().toLowerCase(),
        payer_phone: formData.payer_phone.replace(/\D/g, '') || null,
        payer_cpf: formData.payer_cpf.replace(/\D/g, ''),
        notes: formData.notes.trim() || null
      };

      await onSubmit(subscriberData);
    } catch (error) {
      console.error('Error submitting subscriber:', error);
      
      // Enhanced error handling for contract code conflicts
      const errorMsg = error.message?.toLowerCase() || '';
      const isContractCodeError = errorMsg.includes('código de contrato') || 
                                 (errorMsg.includes('unique constraint') && errorMsg.includes('contract_code'));
      
      console.log('Error detected:', error.message);
      console.log('Is contract code error:', isContractCodeError);
      
      if (isContractCodeError) {
        // Try to auto-generate a new code and retry once
        const selectedStudent = students.find(s => s.id === formData.student_id);
        if (selectedStudent) {
          try {
            toast.loading('Gerando novo código de contrato...', { id: 'regenerating' });
            const newContractCode = await generateUniqueContractCode(selectedStudent);
            
            setFormData(prev => ({
              ...prev,
              contract_code: newContractCode
            }));
            
            // Retry submission with new code
            const retryData = {
              ...subscriberData,
              contract_code: newContractCode
            };
            
            await onSubmit(retryData);
            toast.success('Assinatura criada com novo código!', { id: 'regenerating' });
            return; // Success, exit function
            
          } catch (retryError) {
            toast.error('Erro ao gerar novo código. Use o botão 🔄 manualmente.', { id: 'regenerating' });
            setErrors(prev => ({
              ...prev,
              contract_code: 'Código já existe. Clique no botão 🔄 para gerar um novo código.'
            }));
          }
        } else {
          toast.error(error.message + ' Use o botão de regenerar código.');
          setErrors(prev => ({
            ...prev,
            contract_code: 'Código já existe. Clique no botão 🔄 para gerar um novo código.'
          }));
        }
      } else {
        toast.error('Erro ao salvar assinatura: ' + (error.message || 'Erro desconhecido'));
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return CheckCircle;
      case 'pending': return Clock;
      case 'overdue': return AlertCircle;
      case 'cancelled': return XCircle;
      default: return Clock;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'paid': return 'Pago';
      case 'pending': return 'Pendente';
      case 'overdue': return 'Vencido';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const selectedStudent = students.find(s => s.id === formData.student_id);
  const selectedPlan = plans.find(p => p.id === formData.monthly_plan_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden border border-gray-200">
        {/* Header Moderno com Gradiente */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {initialData ? 'Editar Assinatura' : 'Nova Assinatura'}
                </h2>
                <p className="text-indigo-100 text-sm">
                  {initialData ? 'Modificar dados da mensalidade' : 'Criar nova mensalidade para estudante'}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(92vh-120px)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Layout em Grid Compacto */}
            <div className="p-4 space-y-4">
              
              {/* Seção Principal - Assinatura */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                    <Info className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-800">Dados da Assinatura</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Estudante */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Estudante *
                    </label>
                    <select
                      name="student_id"
                      value={formData.student_id}
                      onChange={handleChange}
                      className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white/80 ${
                        errors.student_id ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                      disabled={loadingData}
                    >
                      <option value="">
                        {loadingData ? 'Carregando...' : 'Selecionar estudante'}
                      </option>
                      {students.map(student => (
                        <option key={student.id} value={student.id}>
                          {student.name} - {student.registration_number}
                        </option>
                      ))}
                    </select>
                    {errors.student_id && (
                      <p className="mt-1 text-xs text-red-600">{errors.student_id}</p>
                    )}
                  </div>

                  {/* Plano */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Plano de Mensalidade *
                    </label>
                    <select
                      name="monthly_plan_id"
                      value={formData.monthly_plan_id}
                      onChange={handleChange}
                      className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white/80 ${
                        errors.monthly_plan_id ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                      disabled={loadingData}
                    >
                      <option value="">Selecionar plano</option>
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - R$ {parseFloat(plan.monthly_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </option>
                      ))}
                    </select>
                    {errors.monthly_plan_id && (
                      <p className="mt-1 text-xs text-red-600">{errors.monthly_plan_id}</p>
                    )}
                  </div>

                  {/* Código do Contrato */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Código do Contrato *
                    </label>
                    <div className="flex">
                      <input
                        name="contract_code"
                        value={formData.contract_code}
                        onChange={handleChange}
                        className={`flex-1 px-2 py-1.5 text-xs border rounded-l-md focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white/80 ${
                          errors.contract_code ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Ex: MENS2024010001"
                        required
                      />
                      <button
                        type="button"
                        onClick={handleRegenerateContractCode}
                        className="px-2 py-1.5 text-xs bg-indigo-500 text-white border border-indigo-500 rounded-r-md hover:bg-indigo-600 focus:ring-1 focus:ring-indigo-500 transition-colors"
                        title="Regenerar código"
                      >
                        🔄
                      </button>
                    </div>
                    {errors.contract_code && (
                      <p className="mt-1 text-xs text-red-600">{errors.contract_code}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white/80"
                    >
                      <option value="active">Ativo</option>
                      <option value="suspended">Suspenso</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>

                  {/* Data de Início */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Data de Início *
                    </label>
                    <input
                      name="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white/80 ${
                        errors.start_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors.start_date && (
                      <p className="mt-1 text-xs text-red-600">{errors.start_date}</p>
                    )}
                  </div>

                  {/* Data de Fim */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Data de Fim (Opcional)
                    </label>
                    <input
                      name="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={handleChange}
                      className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-transparent bg-white/80 ${
                        errors.end_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.end_date && (
                      <p className="mt-1 text-xs text-red-600">{errors.end_date}</p>
                    )}
                  </div>

                </div>
              </div>

              {/* Resumo da Assinatura */}
              {(selectedStudent || selectedPlan) && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3">
                  <h4 className="text-sm font-semibold text-green-900 mb-2 flex items-center">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Resumo da Assinatura
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    {selectedStudent && (
                      <div>
                        <span className="text-green-700 font-medium">Estudante:</span>
                        <div className="text-green-900">
                          {selectedStudent.name}<br />
                          <span className="text-green-600">
                            Mat: {selectedStudent.registration_number}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedPlan && (
                      <div>
                        <span className="text-green-700 font-medium">Plano:</span>
                        <div className="text-green-900">
                          {selectedPlan.name}<br />
                          <span className="text-emerald-600 font-semibold">
                            R$ {parseFloat(selectedPlan.monthly_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedPlan && (
                      <div>
                        <span className="text-green-700 font-medium">Configurações:</span>
                        <div className="text-green-900">
                          Vencimento: dia {selectedPlan.due_day}<br />
                          {selectedPlan.recurrence_months && (
                            <span className="text-green-600">
                              Carnê: {selectedPlan.recurrence_months} meses
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Dados do Responsável Financeiro */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center mb-3">
                <div className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center mr-2">
                  <User className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Responsável Financeiro</h3>
              </div>

              {/* Dropdown sempre visível para selecionar responsável */}
              <div className="mb-3 bg-white/60 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <UserPlus className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-purple-800 font-medium text-xs">
                    Selecionar Responsável Financeiro
                  </span>
                </div>
                  
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Responsável Cadastrado *
                  </label>
                  <select
                    name="selected_parent_id"
                    value={formData.selected_parent_id}
                    onChange={handleChange}
                    className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-purple-500 focus:border-transparent ${
                      errors.selected_parent_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={loadingData}
                    required
                  >
                    <option value="">
                      {loadingData ? 'Carregando...' : 'Selecione um responsável'}
                    </option>
                    {availablePeople.map(person => (
                      <option key={person.id} value={person.id}>
                        {person.name} - {person.email} {person.asaas_customer_id ? '(🔗 Asaas)' : '(⚠️ Sem Asaas)'}
                      </option>
                    ))}
                  </select>
                  {errors.selected_parent_id && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.selected_parent_id}
                    </p>
                  )}
                  
                  {availablePeople.length === 0 && !loadingData && (
                    <p className="mt-1 text-xs text-gray-500">
                      Nenhum responsável cadastrado. 
                      <button 
                        type="button"
                        className="text-purple-600 hover:text-purple-500 ml-1"
                        onClick={() => setFormData(prev => ({ ...prev, use_existing_parent: false }))}
                      >
                        Cadastrar novo responsável
                      </button>
                    </p>
                  )}
                  
                  {availablePeople.length > 0 && !loadingData && (
                    <div className="mt-1 text-xs text-purple-600">
                      💡 <strong>Dica:</strong> Responsáveis "(🔗 Asaas)" já têm integração configurada.
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    name="payer_name"
                    value={formData.payer_name}
                    onChange={handleChange}
                    className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white/80 ${
                      errors.payer_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Nome do responsável pelo pagamento"
                    required
                    disabled={formData.selected_parent_id}
                  />
                  {errors.payer_name && (
                    <p className="mt-1 text-xs text-red-600">{errors.payer_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Email *
                  </label>
                  <input
                    name="payer_email"
                    type="email"
                    value={formData.payer_email}
                    onChange={handleChange}
                    className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white/80 ${
                      errors.payer_email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="email@exemplo.com"
                    required
                    disabled={formData.selected_parent_id}
                  />
                  {errors.payer_email && (
                    <p className="mt-1 text-xs text-red-600">{errors.payer_email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Telefone
                  </label>
                  <input
                    name="payer_phone"
                    value={formData.payer_phone}
                    onChange={handleChange}
                    className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white/80 ${
                      errors.payer_phone ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="(11) 99999-9999"
                    disabled={formData.selected_parent_id}
                    maxLength={15}
                  />
                  {errors.payer_phone && (
                    <p className="mt-1 text-xs text-red-600">{errors.payer_phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    CPF *
                  </label>
                  <input
                    name="payer_cpf"
                    value={formData.payer_cpf}
                    onChange={handleChange}
                    className={`w-full px-2 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white/80 ${
                      errors.payer_cpf ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="000.000.000-00"
                    required
                    disabled={!!formData.selected_parent_id}
                    maxLength={14}
                  />
                  {errors.payer_cpf && (
                    <p className="mt-1 text-xs text-red-600">{errors.payer_cpf}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Lançamentos/Faturas da Assinatura */}
            {initialData && invoices.length > 0 && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center mr-2">
                    <Receipt className="w-4 h-4 text-amber-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-800">
                    Lançamentos da Assinatura ({invoices.length})
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Ref. Mês
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Vencimento
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Valor
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Pagamento
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoices.map((invoice) => {
                        const StatusIcon = getStatusIcon(invoice.status);
                        return (
                          <tr key={invoice.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {new Date(invoice.reference_month).toLocaleDateString('pt-BR', { 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              R$ {parseFloat(invoice.final_amount).toLocaleString('pt-BR', { 
                                minimumFractionDigits: 2 
                              })}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {getStatusText(invoice.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {invoice.payment_date 
                                ? new Date(invoice.payment_date).toLocaleDateString('pt-BR')
                                : '-'
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-purple-700">
                      Total de faturas: {invoices.length}
                    </span>
                    <div className="flex space-x-4">
                      <span className="text-green-700">
                        Pagas: {invoices.filter(i => i.status === 'paid').length}
                      </span>
                      <span className="text-yellow-700">
                        Pendentes: {invoices.filter(i => i.status === 'pending').length}
                      </span>
                      <span className="text-red-700">
                        Vencidas: {invoices.filter(i => i.status === 'overdue').length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Observações */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center mb-3">
                <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center mr-2">
                  <FileText className="w-4 h-4 text-gray-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Observações</h3>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Observações Adicionais
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-500 focus:border-transparent bg-white/80"
                  rows={3}
                  placeholder="Informações adicionais sobre a assinatura..."
                />
              </div>

              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  <strong>Lembrete:</strong> Após criar a assinatura, as faturas serão geradas automaticamente conforme a configuração do plano selecionado.
                </p>
              </div>
            </div>

              {/* Botões */}
              <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {initialData ? 'Atualizar Assinatura' : 'Criar Assinatura'}
                </button>
                
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MonthlySubscriberForm;
